import type { ReactNode } from 'react';
import { Globe } from 'lucide-react';
import { Card, EmptyState, LoadMore, TableSkeleton } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { HostDiscovery } from '@/components/nodes/HostDiscovery';
import { SitesTable } from '@/components/sites/SitesTable';
import { useRoles } from '@/hooks/useAuth';
import { useServerSites } from '@/hooks/useSites';
import { can } from '@/utils/permissions';
import type { NodeDetailView } from '@/types/api';

/**
 * The sites on a server (ADR-0058): those the panel runs there, or watches
 * there for an external one — and on an external server, everything its
 * agent found, marked as in the panel or not, so nothing on it goes unseen.
 */
export function ServerSites({ node }: { readonly node: NodeDetailView }): ReactNode {
  const roles = useRoles();
  const sites = useServerSites(node.id);
  const host = node.role === 'host';
  const items = sites.data?.pages.flatMap((page) => page.items) ?? [];
  const watched = new Set(items.flatMap((site) => (site.primaryDomain ? [site.primaryDomain.replace(/^www\./, '')] : [])));

  return (
    <div className="stack">
      <Card flush title={host ? 'Сайты в панели' : 'Сайты'} icon={Globe}>
        {sites.isPending ? (
          <TableSkeleton rows={3} columns={5} />
        ) : sites.isError ? (
          <ErrorState error={sites.error} />
        ) : items.length === 0 ? (
          <EmptyState icon={Globe} message={host ? 'Сайтов этого сервера в панели пока нет' : 'На сервере пока нет сайтов панели'} />
        ) : (
          <SitesTable sites={items} showServer={false} />
        )}
      </Card>
      {sites.data ? <LoadMore shown={items.length} hasMore={sites.hasNextPage} busy={sites.isFetchingNextPage} onMore={() => void sites.fetchNextPage()} /> : null}

      {host ? (
        <Card>
          <HostDiscovery node={node} mayManage={can(roles, 'nodes.manage')} {...(sites.hasNextPage ? {} : { watched })} />
        </Card>
      ) : null}
    </div>
  );
}
